/**
 * A library clip on the subject — ADR-033 phase 4.
 *
 * World's animation library is animation-only GLBs, one clip each, tracks
 * named for the FAMILY's bones (`Hip.quaternion`, `LeftHandIndex1.quaternion`,
 * …). The packager plays one on the character in the viewport — the
 * selection loop the carousel will show, the pool a pin replaces — so the
 * author sees what World will play before World plays it.
 *
 * Two steps, apart so each can be measured:
 *  - `loadLibraryClip`: bytes → the clip. One clip per file is the library's
 *    rule (ADR-178); a file with several yields its first and says so.
 *  - `bindLibraryClip`: the clip's track names → THIS subject's node names.
 *    The session's grafted bones still wear `_graft` (the export doors strip
 *    it; the scene does not), so a library `LeftHandIndex1` binds to the
 *    scene's `LeftHandIndex1_graft` — the same name rule the exporters apply
 *    in reverse. A track no node carries is DROPPED and named, never left
 *    for three to warn about per frame; a clip that binds nothing refuses.
 *    The hit rate is the machine check: bound / total, with the misses.
 *
 * No retargeting happens here on purpose: a library clip is already on the
 * family's skeleton. A rig of another family fails the family match upstream
 * (the library read) and never reaches this door.
 *
 * ONE exception, and it is World's rule, not a retarget: the hip POSITION
 * track. A family clip carries the bake anchor's absolute hip height (Avery's
 * `Hip` rests at 0.5355; the idle key sits at 0.526). Rotation transfers
 * across bodies; a position does not. Bound raw, the track drives every
 * subject's hips to the ANCHOR's height, and a short-legged body rides up by
 * the difference — Wilbur (hip 0.183) by a third of a unit, measured across
 * the FingerArmed bench 2026-09-06. World scales those keys by the subject's
 * rest hip length over the donor's, read from the skeleton the clip ships
 * (`hipHeightCompensation.ts`, #982/#1102, in the player, the NPC binder and
 * the carousel stage). The preview must show what World plays, so the same
 * ratio is applied here, with the same death exemption (a scaled corpse sank
 * through the floor — World's field verdict).
 *
 * Depends on: three, kernel (RigDescriptor type), load-glb. Depended on by:
 * the app's presentation resolver (main), tests.
 */
import { AnimationClip, PropertyBinding, type KeyframeTrack, type Object3D } from 'three';
import type { RigDescriptor } from '@harryhausen/kernel';
import { loadGLB } from './load-glb.js';
import { parseTrackName, resolvesProperty } from './track-binding.js';

/** Skeleton-side names of the bone whose position keys carry hip height, by family — World's `HIP_BONE_NAMES`, as AUTHORED. */
export const HIP_BONE_NAMES: readonly string[] = ['J_Bip_C_Hips', 'Hips', 'mixamorig:Hips', 'Armature|Hips', 'Hip'];
/**
 * Is this RUNTIME node name a hip? GLTFLoader sanitizes reserved characters
 * out of node names (`mixamorig:Hips` → `mixamorigHips`, `Armature|Hips` →
 * `ArmatureHips` — load-glb.test.ts pins it), and every name this module
 * compares — the donor scan, the subject's rig, the bound track — is a
 * runtime name. So the list is matched in both spellings (Codex P2, #161).
 */
export function isHipBoneName(runtimeName: string): boolean {
  return HIP_BONE_NAMES.some((n) => n === runtimeName || PropertyBinding.sanitizeNodeName(n) === runtimeName);
}
/** Below this rest length a hip node is origin-pinned — refuse to divide (World's `MIN_HIP_REST_LENGTH`). */
export const MIN_HIP_REST_LENGTH = 0.01;
/** Ratios this close to 1 are bake noise; the keys are left alone (World's `HIP_SCALE_EPSILON`). */
export const HIP_SCALE_EPSILON = 0.01;

/** The donor skeleton's hip rest, read from the clip file's own node. */
export interface DonorHipRest {
  readonly node: string;
  /** |rest translation| of the hip node in the file, rig units. */
  readonly length: number;
}

export interface LoadedLibraryClip {
  /** The one clip the file carries (a file with more is refused). */
  readonly clip: AnimationClip;
  /** The hip rest the file ships (World reads the same node), or null when no family hip node is in the file. */
  readonly hipRest: DonorHipRest | null;
}

export interface HipScaleDecision {
  /** Every hip position key was multiplied by this; 1 = left alone. */
  readonly factor: number;
  readonly reason: 'scaled' | 'identity' | 'grounded-pose' | 'unmeasurable';
  readonly donorRestLength: number | null;
  readonly subjectRestLength: number | null;
}

/**
 * Clip names whose hip travel is authored to a FLOOR endpoint — death, and
 * only death (World's `isGroundedPoseKey`: per token, case-insensitive; sit
 * is NOT exempt, measured 2026-09-04 on Gilda).
 */
export function isGroundedPoseClip(clipName: string): boolean {
  return clipName
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((token) => token.startsWith('dead') || token.startsWith('death'));
}

/** World's `resolveHipScale`, verbatim in rule: subject rest ÷ donor rest, death exempt, degenerate refused. */
export function hipScaleDecision(donorRestLength: number | null, subjectRestLength: number | null, clipName: string): HipScaleDecision {
  const base = { donorRestLength, subjectRestLength };
  if (isGroundedPoseClip(clipName)) return { factor: 1, reason: 'grounded-pose', ...base };
  if (
    donorRestLength === null ||
    subjectRestLength === null ||
    !Number.isFinite(donorRestLength) ||
    !Number.isFinite(subjectRestLength) ||
    donorRestLength < MIN_HIP_REST_LENGTH ||
    subjectRestLength < MIN_HIP_REST_LENGTH
  ) {
    return { factor: 1, reason: 'unmeasurable', ...base };
  }
  const factor = subjectRestLength / donorRestLength;
  if (Math.abs(factor - 1) <= HIP_SCALE_EPSILON) return { factor: 1, reason: 'identity', ...base };
  return { factor, reason: 'scaled', ...base };
}

function donorHipRest(scene: Object3D): DonorHipRest | null {
  let found: DonorHipRest | null = null;
  scene.traverse((node) => {
    if (found === null && isHipBoneName(node.name)) found = { node: node.name, length: node.position.length() };
  });
  return found;
}

export class LibraryClipError extends Error {}

export async function loadLibraryClip(bytes: ArrayBuffer): Promise<LoadedLibraryClip> {
  const loaded = await loadGLB(bytes);
  const [clip] = loaded.clips;
  if (clip === undefined) throw new LibraryClipError('the file carries no animation — not a library clip');
  // ADR-178 §3c: one file, one clip, the clipId is the filename. A file with
  // more is not a library clip; playing its first under the requested id
  // would let an author preview and pin an id while watching something else
  // (Codex P2, #151 round two). Refused here, at the only reader.
  if (loaded.clips.length > 1) {
    throw new LibraryClipError(`the file carries ${loaded.clips.length} animations (${loaded.clips.map((c) => c.name).join(', ')}) — a library clip carries one`);
  }
  return { clip, hipRest: donorHipRest(loaded.scene) };
}

export interface BoundLibraryClip {
  readonly clip: AnimationClip;
  /** Tracks that resolved on the subject (renamed ones included). */
  readonly bound: number;
  /** Tracks renamed onto a `_graft` node. */
  readonly renamed: number;
  /** Track names no node on the subject carries — dropped from the playable clip. */
  readonly unresolved: readonly string[];
  readonly total: number;
  /** What happened to the hip position track — null when the clip carries none (or it did not bind). */
  readonly hip: HipScaleDecision | null;
}

/** The facts the hip rule needs: the donor's rest from the file, the subject's from its rig. */
export interface HipScaleInputs {
  readonly donor: DonorHipRest | null;
  /** The subject's RigDescriptor — the hip's `localBind` is the rest World reads from the bone's rest matrix. */
  readonly rig: RigDescriptor;
}

const GRAFT = '_graft';

/**
 * Rename each track to the node the subject actually has, drop what it does
 * not, refuse when nothing binds. The clip handed in is never mutated. A
 * track counts as bound only when the viewport would DRIVE it — the node is
 * there AND the property resolves on it (track-binding.ts): a morph track
 * on a bone, a material path, are unresolved here, not a hit the stage
 * later refuses.
 */
export function bindLibraryClip(clip: AnimationClip, root: Object3D, hipInputs?: HipScaleInputs): BoundLibraryClip {
  const kept: KeyframeTrack[] = [];
  const unresolved: string[] = [];
  let renamed = 0;
  let hip: HipScaleDecision | null = null;
  for (const track of clip.tracks) {
    const { nodeName, propertyName, propertyIndex } = parseTrackName(track.name);
    // three's rule: no node name (a `.morphTargetInfluences[x]` on the root) reaches the root.
    const straight = PropertyBinding.findNode(root, nodeName as string) as Object3D | null;
    if (straight !== null) {
      if (!resolvesProperty(straight, propertyName, propertyIndex)) {
        unresolved.push(track.name);
      } else if (hipInputs !== undefined && hip === null && propertyName === 'position' && isHipBoneName(straight.name)) {
        // The hip height rule (module note): the subject's rest from its rig,
        // the donor's from the file. A hip the rig does not describe is
        // unmeasurable, never a silent raw bind.
        const bone = hipInputs.rig.bones.find((b) => b.name === straight.name);
        const p = bone?.localBind.position;
        const subjectRest = p === undefined ? null : Math.hypot(p.x, p.y, p.z);
        hip = hipScaleDecision(hipInputs.donor?.length ?? null, subjectRest, clip.name);
        if (hip.factor === 1) {
          kept.push(track);
        } else {
          const scaled = track.clone();
          const values = scaled.values;
          for (let i = 0; i < values.length; i++) values[i] = (values[i] as number) * hip.factor;
          kept.push(scaled);
        }
      } else {
        kept.push(track);
      }
      continue;
    }
    const grafted = `${nodeName ?? ''}${GRAFT}`;
    const graft = nodeName === undefined || nodeName === '' ? null : (PropertyBinding.findNode(root, grafted) as Object3D | null);
    if (graft !== null && resolvesProperty(graft, propertyName, propertyIndex)) {
      const t = track.clone();
      t.name = track.name.replace(nodeName as string, grafted);
      kept.push(t);
      renamed += 1;
      continue;
    }
    unresolved.push(track.name);
  }
  // Nothing to drive is a refusal whether the file's tracks all missed or
  // the animation carried none (Codex P2, #151 round five): "0/0 bound" with
  // the stage playing silence is not a preview.
  if (kept.length === 0) {
    throw new LibraryClipError(
      clip.tracks.length === 0
        ? `library clip "${clip.name}" carries no tracks — an empty animation`
        : `library clip "${clip.name}" binds to nothing on "${root.name || '(unnamed)'}" — all ${clip.tracks.length} tracks unresolved (first: ${unresolved[0]})`,
    );
  }
  return {
    clip: new AnimationClip(clip.name, clip.duration, kept),
    bound: kept.length,
    renamed,
    unresolved,
    total: clip.tracks.length,
    hip,
  };
}
